'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';
import { Check, Zap, Crown, Rocket } from 'lucide-react';

export function PricingSection() {
	const plans = [
		{
			name: 'Starter',
			price: 'Free',
			period: 'forever',
			description: 'Perfect for testing and small experiments',
			icon: <Zap className="h-6 w-6 text-blue-600" />,
			features: [
				'1 AI Sales Agent',
				'100 prospect interactions/month',
				'Basic conversation templates',
				'Email integration',
				'Performance analytics',
				'Community support'
			],
			cta: 'Start Free',
			href: '/signup',
			popular: false,
			gradient: false
		},
		{
			name: 'Professional',
			price: '$97',
			period: '/month',
			description: 'For growing businesses ready to scale',
			icon: <Crown className="h-6 w-6 text-purple-600" />,
			features: [
				'5 AI Sales Agents',
				'2,500 prospect interactions/month',
				'Advanced conversation AI',
				'CRM integrations',
				'A/B testing tools',
				'Custom training datasets',
				'Priority support',
				'Advanced analytics'
			],
			cta: 'Start Professional',
			href: '/signup?plan=professional',
			popular: true,
			gradient: true
		},
		{
			name: 'Enterprise',
			price: '$297',
			period: '/month',
			description: 'For companies serious about AI-powered sales',
			icon: <Rocket className="h-6 w-6 text-emerald-600" />,
			features: [
				'Unlimited AI Sales Agents',
				'10,000 prospect interactions/month',
				'Custom AI model training',
				'White-label options',
				'API access',
				'Dedicated success manager',
				'Custom integrations',
				'SLA guarantees'
			],
			cta: 'Contact Sales',
			href: '/contact',
			popular: false,
			gradient: false
		}
	];

	return (
		<section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/40 backdrop-blur-sm">
			<div className="max-w-7xl mx-auto">
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						Simple, Transparent Pricing
					</h2>
					<p className="text-xl text-gray-600 max-w-3xl mx-auto">
						Start free and scale as your AI sales team grows. No hidden fees, no
						long-term contracts.
					</p>
				</div>

				<div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
					{plans.map((plan, index) => (
						<Card
							key={index}
							className={`relative border-2 transition-all duration-300 hover:shadow-xl ${plan.popular
									? 'border-purple-300 shadow-lg bg-gradient-to-br from-purple-50 to-blue-50'
									: 'border-gray-200 hover:border-gray-300 bg-white/60 backdrop-blur-sm'
								}`}
						>
							{plan.popular && (
								<div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
									<Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1">
										Most Popular
									</Badge>
								</div>
							)}

							<CardContent className="p-8">
								<div className="text-center">
									<div className="flex items-center justify-center mb-4">
										{plan.icon}
										<h3 className="text-2xl font-bold text-gray-900 ml-2">
											{plan.name}
										</h3>
									</div>

									<div className="mb-4">
										<span className="text-4xl font-bold text-gray-900">
											{plan.price}
										</span>
										<span className="text-gray-600">{plan.period}</span>
									</div>

									<p className="text-gray-600 mb-6">{plan.description}</p>

									<Link href={plan.href}>
										<Button
											className={`w-full mb-6 ${plan.gradient
													? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
													: 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'
												}`}
											size="lg"
										>
											{plan.cta}
										</Button>
									</Link>

									<div className="space-y-3 text-left">
										{plan.features.map((feature, featureIndex) => (
											<div key={featureIndex} className="flex items-start">
												<Check className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
												<span className="text-gray-700 text-sm">
													{feature}
												</span>
											</div>
										))}
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>

				{/* FAQ Section */}
				<div className="mt-16 text-center">
					<h3 className="text-2xl font-bold text-gray-900 mb-8">
						Frequently Asked Questions
					</h3>
					<div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto text-left">
						<div>
							<h4 className="font-semibold text-gray-900 mb-2">
								How quickly can I get started?
							</h4>
							<p className="text-gray-600 text-sm">
								You can create and train your first AI agent in under 15 minutes.
								Most users see results within their first day.
							</p>
						</div>
						<div>
							<h4 className="font-semibold text-gray-900 mb-2">
								What if I need more interactions?
							</h4>
							<p className="text-gray-600 text-sm">
								All plans can be upgraded instantly. Additional interactions are
								available at $0.10 per interaction above your plan limit.
							</p>
						</div>
						<div>
							<h4 className="font-semibold text-gray-900 mb-2">
								Can I cancel anytime?
							</h4>
							<p className="text-gray-600 text-sm">
								Yes! All paid plans are month-to-month with no long-term
								commitments. Cancel or change plans anytime.
							</p>
						</div>
						<div>
							<h4 className="font-semibold text-gray-900 mb-2">
								Do you offer custom solutions?
							</h4>
							<p className="text-gray-600 text-sm">
								Absolutely. Our Enterprise plan includes custom AI training,
								integrations, and dedicated support for your specific needs.
							</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
